import { ReactNode } from 'react'
import { motion } from 'framer-motion'
import { Briefcase, MapPin, Building2, ExternalLink } from 'lucide-react'

interface CareerPageSettings {
  companyName: string
  companyLogo?: string
  heroTitle?: string
  heroSubtitle?: string
  primaryColor?: string
  backgroundColor?: string
  socialLinks?: {
    linkedin?: string
    twitter?: string
    facebook?: string
    instagram?: string
  }
}

interface CareerPageLayoutProps {
  settings: CareerPageSettings
  children: ReactNode
  tenantSlug: string
}

export function CareerPageLayout({
  settings,
  children,
  tenantSlug,
}: CareerPageLayoutProps) {
  const {
    companyName,
    companyLogo,
    heroTitle = `Join ${companyName}`,
    heroSubtitle = "We're looking for talented people to join our team",
    primaryColor = '#0f172a',
    socialLinks,
  } = settings

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <a href={`/careers/${tenantSlug}`} className="flex items-center gap-3">
              {companyLogo ? (
                <img
                  src={companyLogo}
                  alt={companyName}
                  className="h-8 w-auto"
                />
              ) : (
                <div
                  className="h-8 w-8 rounded-lg flex items-center justify-center text-white font-bold"
                  style={{ backgroundColor: primaryColor }}
                >
                  {companyName.charAt(0)}
                </div>
              )}
              <span className="font-semibold text-lg">{companyName}</span>
            </a>

            <nav className="flex items-center gap-4">
              <a
                href={`/careers/${tenantSlug}`}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                All Jobs
              </a>
              {socialLinks?.linkedin && (
                <a
                  href={socialLinks.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section
        className="relative py-20 overflow-hidden"
        style={{ backgroundColor: primaryColor }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-black/20 to-transparent" />
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center text-white max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{heroTitle}</h1>
            <p className="text-lg md:text-xl opacity-90">{heroSubtitle}</p>
          </motion.div>
        </div>
        {/* Decorative elements */}
        <div className="absolute -bottom-1 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
              fill="hsl(var(--background))"
            />
          </svg>
        </div>
      </section>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t py-12 mt-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {companyLogo ? (
                <img
                  src={companyLogo}
                  alt={companyName}
                  className="h-6 w-auto opacity-50"
                />
              ) : (
                <Building2 className="h-5 w-5 text-muted-foreground" />
              )}
              <span className="text-sm text-muted-foreground">
                © {new Date().getFullYear()} {companyName}. All rights reserved.
              </span>
            </div>
            <div className="flex items-center gap-4">
              {socialLinks?.linkedin && (
                <a
                  href={socialLinks.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  LinkedIn
                </a>
              )}
              {socialLinks?.twitter && (
                <a
                  href={socialLinks.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  Twitter
                </a>
              )}
              <span className="text-sm text-muted-foreground">
                Powered by ATS
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

// Job Card for career page
interface JobCardProps {
  job: {
    id: string
    slug: string
    title: string
    department?: string
    location?: string
    employmentType: string
    remotePolicy: string
    postedAt: string
  }
  tenantSlug: string
}

export function CareerJobCard({ job, tenantSlug }: JobCardProps) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  const formatEmploymentType = (type: string) => {
    return type.split('-').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ')
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      className="group"
    >
      <a
        href={`/careers/${tenantSlug}/jobs/${job.slug || job.id}`}
        className="block p-6 bg-card rounded-xl border hover:border-primary/50 hover:shadow-lg transition-all"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold group-hover:text-primary transition-colors">
              {job.title}
            </h3>

            <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-muted-foreground">
              {job.department && (
                <span className="flex items-center gap-1">
                  <Building2 className="h-3.5 w-3.5" />
                  {job.department}
                </span>
              )}
              {job.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {job.location}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Briefcase className="h-3.5 w-3.5" />
                {formatEmploymentType(job.employmentType)}
              </span>
            </div>
          </div>

          <div className="flex flex-col items-end gap-2">
            <span className="text-xs text-muted-foreground">
              {formatDate(job.postedAt)}
            </span>
            {job.remotePolicy !== 'onsite' && (
              <span className="px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded-full">
                {job.remotePolicy === 'remote' ? 'Remote' : 'Hybrid'}
              </span>
            )}
          </div>
        </div>
      </a>
    </motion.div>
  )
}

// Search and Filter Bar
interface SearchFilterProps {
  searchQuery: string
  onSearchChange: (query: string) => void
  departments: string[]
  selectedDepartment: string
  onDepartmentChange: (dept: string) => void
  locations: string[]
  selectedLocation: string
  onLocationChange: (loc: string) => void
}

export function CareerSearchFilter({
  searchQuery,
  onSearchChange,
  departments,
  selectedDepartment,
  onDepartmentChange,
  locations,
  selectedLocation,
  onLocationChange,
}: SearchFilterProps) {
  return (
    <div className="flex flex-col md:flex-row gap-4 mb-8">
      {/* Search */}
      <div className="flex-1">
        <input
          type="text"
          placeholder="Search jobs..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full h-11 px-4 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
        />
      </div>

      {/* Department Filter */}
      {departments.length > 0 && (
        <select
          value={selectedDepartment}
          onChange={(e) => onDepartmentChange(e.target.value)}
          className="h-11 px-4 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
        >
          <option value="">All Departments</option>
          {departments.map((dept) => (
            <option key={dept} value={dept}>
              {dept}
            </option>
          ))}
        </select>
      )}

      {/* Location Filter */}
      {locations.length > 0 && (
        <select
          value={selectedLocation}
          onChange={(e) => onLocationChange(e.target.value)}
          className="h-11 px-4 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
        >
          <option value="">All Locations</option>
          {locations.map((loc) => (
            <option key={loc} value={loc}>
              {loc}
            </option>
          ))}
        </select>
      )}
    </div>
  )
}
